import { FormEvent, useCallback, useEffect, useRef, useState } from 'react'
import { Head, router, usePage } from '@inertiajs/react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Mail, Globe, ArrowRight, Loader2, ArrowLeft } from 'lucide-react'
import AuthLayout from '@/components/AuthLayout'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface ForgotPasswordProps {
  showSubdomain: boolean
  recaptchaSiteKey: string
  resetPasswordUrl: string
  loginUrl: string
  locale: string
  logoUrl: string
  translations: {
    forgotPassword: string
    forgotPasswordDesc: string
    subdomain: string
    email: string
    send: string
    backToLogin: string
  }
}

interface SharedProps {
  flash: { alert?: string; notice?: string }
  errors: Record<string, string[]>
  [key: string]: unknown
}

/* ------------------------------------------------------------------ */
/*  ReCaptcha helpers                                                  */
/* ------------------------------------------------------------------ */

declare global {
  interface Window {
    grecaptcha?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => number
      getResponse: (widgetId?: number) => string
      reset: (widgetId?: number) => void
    }
    onRecaptchaLoad?: () => void
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function ForgotPassword({
  showSubdomain,
  recaptchaSiteKey,
  resetPasswordUrl,
  loginUrl,
  locale,
  logoUrl,
  translations: t,
}: ForgotPasswordProps) {
  const { errors } = usePage<SharedProps>().props

  const [email, setEmail] = useState('')
  const [subdomain, setSubdomain] = useState('')
  const [processing, setProcessing] = useState(false)

  /* ---- recaptcha ---- */
  const recaptchaRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<number | null>(null)
  const [recaptchaToken, setRecaptchaToken] = useState('')

  const renderWidget = useCallback(() => {
    if (recaptchaRef.current && window.grecaptcha && widgetIdRef.current === null) {
      widgetIdRef.current = window.grecaptcha.render(recaptchaRef.current, {
        sitekey: recaptchaSiteKey,
        callback: (token: string) => setRecaptchaToken(token),
        'expired-callback': () => setRecaptchaToken(''),
      })
    }
  }, [recaptchaSiteKey])

  useEffect(() => {
    if (!recaptchaSiteKey) return
    if (window.grecaptcha) {
      renderWidget()
      return
    }
    window.onRecaptchaLoad = renderWidget
    const script = document.createElement('script')
    script.src =
      'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit'
    script.async = true
    script.defer = true
    document.head.appendChild(script)
    return () => {
      window.onRecaptchaLoad = undefined
    }
  }, [renderWidget, recaptchaSiteKey])

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setProcessing(true)
    router.post(
      resetPasswordUrl,
      {
        user: { email },
        ...(showSubdomain ? { subdomain } : {}),
        'g-recaptcha-response': recaptchaToken,
      },
      {
        onFinish: () => {
          setProcessing(false)
          if (window.grecaptcha && widgetIdRef.current !== null) {
            window.grecaptcha.reset(widgetIdRef.current)
            setRecaptchaToken('')
          }
        },
      },
    )
  }

  const resourceErrors = errors?.email ? errors.email : []

  return (
    <>
      <Head title={t.forgotPassword} />

      <AuthLayout locale={locale} logoUrl={logoUrl}>
        <div className="rounded-2xl bg-white shadow-xl shadow-slate-200/60 border border-slate-100 p-8 sm:p-10">
          <div className="text-center mb-8">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#2DD4BF]/10 to-[#8B5CF6]/10">
              <Mail className="h-7 w-7 text-[#8B5CF6]" />
            </div>
            <h1 className="text-2xl font-semibold text-[#252854] mb-1">{t.forgotPassword}</h1>
            <p className="text-sm text-slate-400">{t.forgotPasswordDesc}</p>
          </div>

          {/* Resource errors */}
          {resourceErrors.length > 0 && (
            <Alert variant="destructive" className="mb-5 rounded-xl border-red-200 bg-red-50/80">
              <AlertDescription>{resourceErrors.join(', ')}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {showSubdomain && (
              <div className="space-y-1.5">
                <Label htmlFor="subdomain" className="text-xs font-medium text-slate-500">
                  {t.subdomain}
                </Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                  <Input
                    id="subdomain"
                    value={subdomain}
                    onChange={(e) => setSubdomain(e.target.value)}
                    className="pl-10 h-11 rounded-xl border-slate-200 bg-slate-50/50 focus:bg-white transition-colors"
                    autoFocus
                    autoComplete="organization"
                  />
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-medium text-slate-500">
                {t.email}
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11 rounded-xl border-slate-200 bg-slate-50/50 focus:bg-white transition-colors"
                  autoComplete="email"
                  autoFocus={!showSubdomain}
                  required
                />
              </div>
            </div>

            {recaptchaSiteKey && (
              <div className="flex justify-center">
                <div ref={recaptchaRef} />
              </div>
            )}

            <Button
              type="submit"
              disabled={processing}
              className="w-full h-11 rounded-xl text-sm font-medium bg-gradient-to-r from-[#2DD4BF] to-[#8B5CF6] hover:from-[#14B8A6] hover:to-[#7C3AED] text-white shadow-lg shadow-[#8B5CF6]/20 transition-all duration-200"
            >
              {processing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <span className="flex items-center gap-2">
                  {t.send}
                  <ArrowRight className="h-4 w-4" />
                </span>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <a
              href={loginUrl}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[#8B5CF6] hover:text-[#7C3AED] transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {t.backToLogin}
            </a>
          </div>
        </div>
      </AuthLayout>
    </>
  )
}
